import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  PictureAsPdf,
  People,
  Assessment,
  CalendarMonth,
  PersonOff,
  Refresh,
  FilterList,
  Business,
  TrendingUp,
  Info
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LabelList
} from 'recharts';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';

// Import Services
import { getRole } from '../roles/role';
import { getRecruitmentReport } from '../services/reportingAPIs';
import { getEmployeeImage } from '../services/HRstaff';

// Import Logo
import marabesLogo from '../assets/images/logos/Marabes.png';

// === CONSTANTS & CONFIGURATION ===
const DEFAULT_OFFICES = ['Nador', 'Tangier'];

const NOT_PLANNED_STATUSES = ['Not Planned Yet'];
const PENDING_STATUSES = ['Pending', '1st Interview (Online)', '2nd Interview', '3rd Interview'];
const DECLINED_STATUSES = ['Declined by HR', 'Declined by Manager', 'Candidate Declined the Offer'];
const HIRED_STATUSES = ['Hired'];
const RESERVED_STATUSES = ['Reserved List'];

// === PROFESSIONAL THEME PALETTES ===
const LIGHT_THEME = {
  bg: '#F8FAFC',          // Very Light Blue-Grey Background
  surface: '#FFFFFF',     // Pure White Cards
  primary: '#1E293B',     // Slate 800 (Dark Text)
  secondary: '#64748B',   // Slate 500 (Muted Text)
  accent: '#3B82F6',      // Corporate Blue
  tableHeaderBg: '#E2E8F0',
  borderColor: '#F1F5F9',
  tableRowHover: '#F8FAFC',
  charts: {
    primary: '#0F172A',   // Navy
    secondary: '#3B82F6', // Blue
    accent: '#F59E0B',    // Amber
    teal: '#10B981',      // Emerald
    danger: '#EF4444',    // Red
  }
};

const DARK_THEME = {
  bg: '#0f172a',          // Dark Background matching body
  surface: '#1e293b',     // Dark Surface matching cards
  primary: '#FFFFFF',     // White text
  secondary: '#94a3b8',   // Light Grey text
  accent: '#38bdf8',      // Corporate Blue
  tableHeaderBg: '#334155', // Darker header
  borderColor: '#475569',
  tableRowHover: '#334155',
  charts: {
    primary: '#E2E8F0',   // Light Navy equivalent
    secondary: '#3B82F6', // Blue
    accent: '#F59E0B',    // Amber
    teal: '#10B981',      // Emerald
    danger: '#EF4444',    // Red
  }
};

const PIE_COLORS = ['#3B82F6', '#F59E0B', '#64748B'];
const OUTCOME_COLORS = ['#22C55E', '#EF4444']; // Green for Hired, Red for Declined 

// === HELPERS ===
const normalizeOffice = (name) => name?.toLowerCase().replace(/\s+/g, '') || 'unknown';

const getStandardOffice = (name) => {
  const norm = normalizeOffice(name);
  if (norm === 'nador') return 'nador';
  if (['tanger', 'tangier'].includes(norm)) return 'tangier';
  return 'unknown';
};

const getFormattedDate = (date) => {
  if (!date) return '';
  // Handle plain YYYY-MM-DD strings from the backend without UTC timezone shift
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
};

// Check if a value is a valid, complete Date object
const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

const getMonthRange = (referenceDate) => {
  const start = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  const end = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const parseDate = (dateVal) => {
  if (!dateVal) return null;
  if (Array.isArray(dateVal)) {
    // [year, month, day, hours, minutes, seconds, nanos]
    const [year, month, day, hours = 0, minutes = 0, seconds = 0] = dateVal;
    return new Date(year, month - 1, day, hours, minutes, seconds);
  }
  const parsed = new Date(dateVal);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const isDateInRange = (dateStr, startDate, endDate) => {
  const target = parseDate(dateStr);
  if (!target || !startDate || !endDate) return false;
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);
  return target >= start && target <= end;
};

// Helper for exact day comparison
const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

// Delay helper for PDF export
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getBase64FromUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Could not load image for PDF:", url);
    return null;
  }
};

const getDepartmentName = (emp) => (emp.department?.name || '').trim().toLowerCase();

const transformStatus = (apiStatus) => {
  if (typeof apiStatus === 'string') {
    const normalized = apiStatus.trim();
    const knownFrontend = [
      'Not Planned Yet', 'Not Attended', 'Pending',
      '1st Interview (Online)', '2nd Interview', '3rd Interview',
      'Hired', 'Declined by HR', 'Declined by Manager', 'Reserved List', 'Candidate Declined the Offer',
    ];
    if (knownFrontend.includes(normalized)) return normalized;
  }
  const map = {
    'NOT_PLANNED_YET': 'Not Planned Yet',
    'NOT_ATTENDED': 'Not Attended',
    'PENDING': 'Pending',
    'INTERVIEW_1': '1st Interview (Online)',
    'INTERVIEW_2': '2nd Interview', 'SECOND_INTERVIEW': '2nd Interview',
    'INTERVIEW_3': '3rd Interview', 'THIRD_INTERVIEW': '3rd Interview',
    'HIRED': 'Hired',
    'DECLINED_BY_HR': 'Declined by HR',
    'DECLINED_BY_MANAGER': 'Declined by Manager',
    'RESERVED_LIST': 'Reserved List',
    'CANDIDATE_DECLINED_THE_OFFER': 'Candidate Declined the Offer',
    'DECLINED_OFFER': 'Candidate Declined the Offer',
    'Candidate_Declined_the_Offre': 'Candidate Declined the Offer',
  };
  return map[apiStatus] || 'Not Planned Yet';
};

// === STYLES FACTORY ===
const getStyles = (theme) => ({
  container: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
    backgroundColor: theme.bg,
    color: theme.primary,
    paddingBottom: '60px',
  },
  // Header
  headerContainer: {
    backgroundColor: theme.surface,
    padding: '24px 40px',
    borderBottom: `1px solid ${theme.borderColor}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  headerTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: theme.primary,
    letterSpacing: '-0.02em',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  headerSubtitle: {
    fontSize: '0.9rem',
    color: theme.secondary,
    marginTop: '4px',
    fontWeight: '500'
  },
  // Controls
  controlsContainer: {
    margin: '0 40px 30px 40px',
    backgroundColor: theme.surface,
    padding: '16px 24px',
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
    border: `1px solid ${theme.borderColor}`
  },
  // Layout
  mainContentGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    padding: '0 40px',
  },
  section: {
    marginBottom: '10px',
    pageBreakInside: 'avoid'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: `2px solid ${theme.tableHeaderBg}`,
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: theme.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  // Split Layout
  splitLayout: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    alignItems: 'stretch'
  },
  tableContainer: {
    flex: '2 1 600px',
    minWidth: '500px',
  },
  chartContainer: {
    flex: '1 1 350px',
    minWidth: '350px',
    backgroundColor: theme.surface,
    borderRadius: '16px',
    border: `1px solid ${theme.borderColor}`,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
  },
  // Cards & Tables
  card: {
    backgroundColor: theme.surface,
    borderRadius: '16px',
    border: `1px solid ${theme.borderColor}`,
    overflow: 'hidden',
    height: '100%',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0',
    minWidth: '600px',
  },
  thead: {
    backgroundColor: theme.tableHeaderBg,
  },
  th: {
    padding: '16px 20px',
    fontSize: '0.75rem',
    fontWeight: '700',
    color: theme.primary,
    textAlign: 'left',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${theme.borderColor}`,
  },
  td: {
    padding: '16px 20px',
    fontSize: '0.9rem',
    color: theme.primary,
    borderBottom: `1px solid ${theme.borderColor}`,
    fontWeight: '500'
  },
  totalRow: {
    backgroundColor: theme.tableHeaderBg,
    fontWeight: '700',
  },
  variancePositive: { color: theme.charts.danger, fontWeight: '700' },
  varianceNegative: { color: theme.charts.teal, fontWeight: '700' },

  // Buttons
  primaryButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: theme.accent,
    color: 'white',
    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
    transition: 'opacity 0.2s'
  },
  secondaryButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: `1px solid ${theme.borderColor}`,
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: theme.surface,
    color: theme.primary,
    transition: 'background 0.2s'
  },
  footer: {
    marginTop: 'auto',
    textAlign: 'center',
    paddingTop: '40px',
    color: theme.secondary,
    fontSize: '0.8rem',
  }
});

// Custom Tooltip for Recharts
const CustomTooltip = ({ active, payload, label, currentTheme }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: currentTheme?.surface || 'white',
        border: `1px solid ${currentTheme?.borderColor || '#E2E8F0'}`,
        padding: '12px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold', fontSize: '0.85rem', color: currentTheme?.primary || '#1E293B' }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: '4px 0 0', fontSize: '0.8rem', color: entry.color }}>
            {entry.name}: <span style={{ fontWeight: 700 }}>{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Custom Renderer for Pie Chart Labels
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }, currentTheme) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 20;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={currentTheme?.primary || '#1E293B'}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CandidateReportGenerator = () => {
  const [reportRawData, setReportRawData] = useState({
    pipelineData: [],
    summaryData: [],
    applicationsToday: [],
    offboardings: []
  });
  // console.log("reportRawData", reportRawData);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Modal & Override State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [overrides, setOverrides] = useState({});

  // Roles
  const userRole = getRole(localStorage.getItem('userRole'));
  const canEdit = userRole === 'HR' || userRole === 'MANAGER' || userRole === 'ADMIN';

  // Refs for logic
  const dashboardContentRef = useRef();

  // Theme logic
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark' && !isGeneratingPDF;
  const currentTheme = isDark ? DARK_THEME : LIGHT_THEME;
  const styles = getStyles(currentTheme);

  // Ref to hold the debounce timer
  const debounceRef = useRef(null);
  // Ref to cache fetched employee base64 images to prevent duplicate 429 rate limit triggers
  const imageCacheRef = useRef(new Map());

  const fetchReportData = useCallback(async (from, to) => {
    // Guard: only fire when both dates are valid complete Date objects
    if (!isValidDate(from) || !isValidDate(to)) {
      console.log('[FRONTEND COMPONENT] fetchReportData skipped - invalid dates:', { from, to });
      return;
    }
    console.log('[FRONTEND COMPONENT] fetchReportData triggered with:', { from, to });
    setLoading(true);
    try {
      const data = await getRecruitmentReport(from, to);

      const rawOffboardings = data?.offboardings || [];
      const batchSize = 3;
      const transformedOffboardings = [];

      for (let i = 0; i < rawOffboardings.length; i += batchSize) {
        const chunk = rawOffboardings.slice(i, i + batchSize);
        const chunkResults = await Promise.all(chunk.map(async off => {
          let imageUrl = null;
          if (off.employeeId) {
            if (imageCacheRef.current.has(off.employeeId)) {
              imageUrl = imageCacheRef.current.get(off.employeeId);
            } else {
              try {
                const blobUrl = await getEmployeeImage(off.employeeId);
                if (blobUrl) {
                  imageUrl = await getBase64FromUrl(blobUrl);
                  if (imageUrl) {
                    imageCacheRef.current.set(off.employeeId, imageUrl);
                  }
                }
              } catch (e) {
                console.warn('Failed to fetch image for offboarding employee', off.employeeId, e);
              }
            }
          }
          return {
            ...off,
            imageUrl
          };
        }));
        transformedOffboardings.push(...chunkResults);
      }

      setReportRawData({
        pipelineData: data?.pipelineData || [],
        summaryData: data?.summaryData || [],
        applicationsToday: data?.applicationsToday || [],
        offboardings: transformedOffboardings
      });
    } catch (err) {
      console.error('Error fetching recruitment report: ', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced effect: wait 400ms after the last date change before calling the API
  // This prevents flooding the API during typing in the DatePicker fields
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchReportData(startDate, endDate);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [startDate, endDate, fetchReportData]);

  // === DATA PREPARATION & OVERRIDES ===

  const appsTodayData = (reportRawData.applicationsToday || []).map(d => ({
    source: d.source,
    Nador: d.nador,
    Tangier: d.tangier,
    Total: d.total
  }));

  const pieTodayData = appsTodayData.map((d, idx) => ({
    name: d.source,
    value: d.Total,
    fill: PIE_COLORS[idx % PIE_COLORS.length]
  })).filter(d => d.value > 0);

  const dynamicOffices = useMemo(() => {
    const summaryOffices = (reportRawData.summaryData || []).map(d => d.office).filter(Boolean);
    const pipelineOffices = (reportRawData.pipelineData || []).map(d => d.office).filter(Boolean);
    const combined = Array.from(new Set([...summaryOffices, ...pipelineOffices]));
    return combined.length > 0 ? combined : DEFAULT_OFFICES;
  }, [reportRawData]);

  const createDynamicRows = (definitions) => {
    return definitions.map(def => {
      const row = { label: def.label, note: def.note, total: 0, _key: def.key };
      dynamicOffices.forEach(office => {
        const officeData = (reportRawData.pipelineData || []).find(d => d.office === office);
        const overrideKey = `${def.key}-${office}`;
        let val = officeData ? officeData[def.key] : 0;

        if (overrides && overrides[overrideKey] !== undefined) {
          val = Number(overrides[overrideKey]);
        }

        row[office] = val;
        row.total += val;
      });
      return row;
    });
  };

  const pipelineRows = createDynamicRows([
    { label: 'Total Application', key: 'totalApp' },
    { label: 'Application Not planned yet', key: 'notPlanned' },
    { label: 'Pending interviews', key: 'pending' },
  ]);

  const outcomeRows = createDynamicRows([
    { label: 'Reserved list', key: 'reserved', note: '(All Time)' },
    { label: 'Hired in period', key: 'hired', note: `(${getFormattedDate(startDate)} - ${getFormattedDate(endDate)})` },
    { label: 'Declined in period', key: 'declined', note: `(${getFormattedDate(startDate)} - ${getFormattedDate(endDate)})` },
  ]);

  const summaryData = dynamicOffices.map(office => {
    const rawRow = (reportRawData.summaryData || []).find(d => d.office === office) || {};

    let currentOps = rawRow.currentOps || 0;
    let hiredPending = rawRow.hiredPending || 0;
    let hqStaff = rawRow.hqStaff || 0;
    let facilityStaff = rawRow.facilityStaff || 0;
    let goal = rawRow.goal || 0;

    // Apply Overrides
    if (overrides[`summary-currentOps-${office}`] !== undefined) currentOps = Number(overrides[`summary-currentOps-${office}`]);
    if (overrides[`summary-hiredPending-${office}`] !== undefined) hiredPending = Number(overrides[`summary-hiredPending-${office}`]);
    if (overrides[`summary-hqStaff-${office}`] !== undefined) hqStaff = Number(overrides[`summary-hqStaff-${office}`]);
    if (overrides[`summary-facilityStaff-${office}`] !== undefined) facilityStaff = Number(overrides[`summary-facilityStaff-${office}`]);
    if (overrides[`summary-goal-${office}`] !== undefined) goal = Number(overrides[`summary-goal-${office}`]);

    const totalStaff = currentOps + hiredPending + hqStaff + facilityStaff;
    const staffForGoals = totalStaff - facilityStaff;
    const variance = goal - staffForGoals;

    return {
      office,
      currentOps,
      hiredPending,
      hqStaff,
      facilityStaff,
      totalStaff,
      staffForGoals,
      goal,
      variance: Math.abs(variance),
      varianceRaw: variance
    };
  });

  const summaryTotal = {
    currentOps: summaryData.reduce((sum, r) => sum + r.currentOps, 0),
    hiredPending: summaryData.reduce((sum, r) => sum + r.hiredPending, 0),
    hqStaff: summaryData.reduce((sum, r) => sum + r.hqStaff, 0),
    facilityStaff: summaryData.reduce((sum, r) => sum + r.facilityStaff, 0),
    totalStaff: summaryData.reduce((sum, r) => sum + r.totalStaff, 0),
    staffForGoals: summaryData.reduce((sum, r) => sum + r.staffForGoals, 0),
    goal: summaryData.reduce((sum, r) => sum + r.goal, 0),
  };

  summaryTotal.variance = Math.abs(summaryTotal.goal - summaryTotal.staffForGoals);

  const workforceChartData = summaryData.map(d => ({
    name: d.office,
    'Current Staff': d.staffForGoals,
    'Goal': d.goal
  }));

  const pipelineChartData = [
    { name: 'Not Planned', value: pipelineRows.find(r => r._key === 'notPlanned')?.total || 0 },
    { name: 'Pending', value: pipelineRows.find(r => r._key === 'pending')?.total || 0 },
    { name: 'Reserved', value: outcomeRows.find(r => r._key === 'reserved')?.total || 0 },
  ].filter(d => d.value > 0);

  const outcomesChartData = [
    { name: 'Hired', value: outcomeRows.find(r => r.label.includes('Hired'))?.total || 0 },
    { name: 'Declined', value: outcomeRows.find(r => r.label.includes('Declined'))?.total || 0 }
  ].filter(d => d.value > 0);

  const filteredOffboardings = reportRawData.offboardings || [];

  const exportPDF = async () => {
    setIsGeneratingPDF(true);
    setLoading(true);

    // Temporarily remove the data-theme="dark" attribute from html/body elements.
    // This prevents global CSS overrides (like [data-theme="dark"] table) from enforcing dark tables on the exported PDF.
    const rootTheme = document.documentElement.getAttribute('data-theme');
    const bodyTheme = document.body.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    document.body.setAttribute('data-theme', 'light');

    try {
      // 1. Add Delay to ensure React renders everything fully
      await wait(1500);

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const pageHeight = doc.internal.pageSize.height;

      const axoLogoUrl = '/cLogo.png';
      const axoBase64 = await getBase64FromUrl(axoLogoUrl);

      // PDF HEADER
      const addHeader = (pageNum) => {
        if (pageNum > 1) doc.addPage();

        // Simple Clean PDF Header
        doc.setFillColor(243, 244, 246);
        doc.rect(0, 0, pageWidth, 25, 'F');

        try { doc.addImage(marabesLogo, 'PNG', margin, 7, 30, 10); } catch (e) { }
        if (axoBase64) doc.addImage(axoBase64, 'PNG', pageWidth - margin - 30, 7, 30, 10);

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text('RECRUITMENT & STAFFING REPORT', pageWidth / 2, 16, { align: 'center' });


      };

      addHeader(1);
      let y = 30;

      if (dashboardContentRef.current) {
        const canvas = await html2canvas(dashboardContentRef.current, { scale: 2, useCORS: true, logging: false });
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const availableHeight = pageHeight - 40;

        if (imgHeight > availableHeight) {
          const scaleFactor = availableHeight / imgHeight;
          doc.addImage(imgData, 'PNG', margin, y, imgWidth * scaleFactor, availableHeight);
        } else {
          doc.addImage(imgData, 'PNG', margin, y, imgWidth, imgHeight);
        }
      }

      doc.save(`Recruitment & Staffing Report-${getFormattedDate(endDate)}.pdf`);
    } catch (err) {
      alert('Failed to generate PDF: ' + err.message);
    } finally {
      // Restore the original data-theme attributes
      if (rootTheme) document.documentElement.setAttribute('data-theme', rootTheme);
      else document.documentElement.removeAttribute('data-theme');

      if (bodyTheme) document.body.setAttribute('data-theme', bodyTheme);
      else document.body.removeAttribute('data-theme');

      setIsGeneratingPDF(false);
      setLoading(false);
    }
  };

  const handleOverrideChange = (key, value) => {
    setOverrides(prev => ({
      ...prev,
      [key]: value === '' ? undefined : Number(value)
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div style={styles.container}>

        {/* === HEADER === */}
        <div style={styles.headerContainer}>
          <div style={styles.headerTitle}>
            <Assessment style={{ fontSize: 32, color: currentTheme.accent }} />
            <div>
              Recruitment Analytics
              <div style={styles.headerSubtitle}>
                Overview • {getFormattedDate(startDate)} to {getFormattedDate(endDate)}
              </div>
            </div>
          </div>
        </div>

        {/* === CONTROLS === */}
        <div className="no-print" style={styles.controlsContainer}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: currentTheme.secondary, fontSize: '0.9rem', fontWeight: 600 }}>
            <FilterList fontSize="small" /> Report Filters:
          </div>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            format="dd-MM-yyyy"
            slotProps={{ textField: { size: 'small', variant: 'outlined', sx: { width: 140, bgcolor: 'background.paper', borderRadius: 1 } } }}
          />
          <span style={{ color: currentTheme.borderColor }}>—</span>
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            format="dd-MM-yyyy"
            slotProps={{ textField: { size: 'small', variant: 'outlined', sx: { width: 140, bgcolor: 'background.paper', borderRadius: 1 } } }}
          />

          <div style={{ flex: 1 }}></div>

          <button onClick={() => fetchReportData(startDate, endDate)} disabled={loading} style={styles.secondaryButton}>
            <Refresh style={{ fontSize: '1.1rem' }} /> {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button onClick={() => setIsEditModalOpen(true)} style={styles.secondaryButton}>
            <Assessment style={{ fontSize: '1.1rem' }} /> Edit Report Data
          </button>
          <button onClick={() => setIsInfoModalOpen(!isInfoModalOpen)} style={styles.secondaryButton}>
            <Info style={{ fontSize: '1.1rem' }} /> Info
          </button>
          <button onClick={exportPDF} style={styles.primaryButton}>
            <PictureAsPdf style={{ fontSize: '1.1rem' }} /> Export PDF
          </button>
        </div>

        {/* === DASHBOARD CONTENT === */}
        <div ref={dashboardContentRef} style={styles.mainContentGroup}>

          {/* 1. Workforce Staffing Summary */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Business style={{ color: currentTheme.accent }} />
              <div style={styles.sectionTitle}>Workforce Staffing</div>
            </div>

            <div style={styles.card}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={styles.th}>Location</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Current Staff</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Hired <span style={{ opacity: 0.6, fontWeight: 400 }}>(Pending)</span></th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>HQ Staff</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Facility</th>
                    <th style={{ ...styles.th, textAlign: 'center', color: currentTheme.primary }}>Total Staff</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.map((row) => (
                    <tr key={row.office} style={{ transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = currentTheme.tableRowHover} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <td style={{ ...styles.td, fontWeight: '600', color: currentTheme.primary }}>{row.office}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{row.currentOps}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{row.hiredPending}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{row.hqStaff}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{row.facilityStaff}</td>
                      <td style={{ ...styles.td, fontWeight: '700', textAlign: 'center', backgroundColor: currentTheme.tableHeaderBg }}>{row.totalStaff}</td>
                    </tr>
                  ))}
                  <tr style={styles.totalRow}>
                    <td style={{ ...styles.td, fontWeight: '700' }}>TOTAL</td>
                    <td style={{ ...styles.td, fontWeight: '700', textAlign: 'center' }}>{summaryTotal.currentOps}</td>
                    <td style={{ ...styles.td, fontWeight: '700', textAlign: 'center' }}>{summaryTotal.hiredPending}</td>
                    <td style={{ ...styles.td, fontWeight: '700', textAlign: 'center' }}>{summaryTotal.hqStaff}</td>
                    <td style={{ ...styles.td, fontWeight: '700', textAlign: 'center' }}>{summaryTotal.facilityStaff}</td>
                    <td style={{ ...styles.td, fontWeight: '800', textAlign: 'center', color: currentTheme.accent }}>{summaryTotal.totalStaff}</td>
                  </tr>
                </tbody>
              </table>
              {isInfoModalOpen && !isGeneratingPDF && (
                <div className="no-print" style={{ margin: '16px', padding: '16px', backgroundColor: currentTheme.bg, border: `1px dashed ${currentTheme.accent}`, borderRadius: '8px', fontSize: '0.85rem', color: currentTheme.secondary }}>
                  <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>Current Staff:</strong> Active Employees assigned to the office, excluding "HQ" and "Facility".</div>
                  <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>Hired (Pending):</strong> Active in-progress onboardings for the office created on or before the End Date.</div>
                  <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>HQ Staff / Facility:</strong> Active Employees strictly in those departments.</div>
                  <div><strong style={{ color: currentTheme.primary }}>Total Staff:</strong> Sum of Current Staff + Hired (Pending) + HQ Staff + Facility.</div>
                </div>
              )}
            </div>
          </div>

          {/* 2. Staffing Goals & Chart */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <TrendingUp style={{ color: currentTheme.accent }} />
              <div style={styles.sectionTitle}>Goals vs. Actual</div>
            </div>

            <div style={styles.splitLayout}>
              <div style={styles.tableContainer}>
                <div style={styles.card}>
                  <table style={styles.table}>
                    <thead style={styles.thead}>
                      <tr>
                        <th style={{ ...styles.th, width: '40%' }}>Location</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Total Staff</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Target Goal</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.map((row) => (
                        <tr key={row.office} style={{ transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = currentTheme.tableRowHover} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <td style={{ ...styles.td, fontWeight: '600' }}>{row.office}</td>
                          {/* UPDATED: Displays staffForGoals (Excludes Facility) */}
                          <td style={{ ...styles.td, textAlign: 'center' }}>{row.staffForGoals}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{row.goal}</td>
                          <td style={{ ...styles.td, textAlign: 'center', ...(row.varianceRaw < 0 ? styles.varianceNegative : styles.variancePositive) }}>
                            {row.varianceRaw > 0 ? `${row.variance}` : `${row.variance}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {isInfoModalOpen && !isGeneratingPDF && (
                    <div className="no-print" style={{ margin: '16px', padding: '16px', backgroundColor: currentTheme.bg, border: `1px dashed ${currentTheme.accent}`, borderRadius: '8px', fontSize: '0.85rem', color: currentTheme.secondary }}>
                      <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>Target Goal:</strong> Official goal for the office.</div>
                      <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>Total Staff:</strong> Uses the Total Staff from above, but <b>subtracts Facility staff</b>.</div>
                      <div><strong style={{ color: currentTheme.primary }}>Variance:</strong> Difference between Target Goal and the Staff count for Goals.</div>
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.chartContainer}>
                <div style={{ fontSize: '0.8rem', fontWeight: '700', color: currentTheme.secondary, marginBottom: '16px', textTransform: 'uppercase' }}>
                  Goal Visualization
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={workforceChartData} barGap={4} margin={{ top: 30, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={currentTheme.borderColor} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: currentTheme.secondary }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: currentTheme.secondary }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip currentTheme={currentTheme} />} cursor={{ fill: currentTheme.palette?.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }} />
                    <Legend iconType="circle" wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }} />
                    <Bar dataKey="Current Staff" fill={currentTheme.charts.primary} radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false}>
                      <LabelList dataKey="Current Staff" position="top" fill={currentTheme.primary} fontSize={12} fontWeight={700} />
                    </Bar>
                    <Bar dataKey="Goal" fill={currentTheme.charts.teal} radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false}>
                      <LabelList dataKey="Goal" position="top" fill={currentTheme.primary} fontSize={12} fontWeight={700} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* 3. Pipeline & Chart */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <CalendarMonth style={{ color: currentTheme.accent }} />
              <div style={styles.sectionTitle}>Recruitment Funnel Overview</div>
            </div>

            <div style={styles.splitLayout}>
              <div style={styles.tableContainer}>
                <div style={styles.card}>
                  <table style={styles.table}>
                    <thead style={styles.thead}>
                      <tr>
                        <th style={{ ...styles.th, width: '40%' }}>Metric</th>
                        {dynamicOffices.map(office => (<th key={office} style={{ ...styles.th, textAlign: 'center' }}>{office}</th>))}
                        <th style={{ ...styles.th, textAlign: 'center' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pipelineRows.map((row, index) => (
                        <tr key={index} style={{ transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = currentTheme.tableRowHover} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <td style={{ ...styles.td, fontWeight: '600', color: currentTheme.primary }}>{row.label}</td>
                          {dynamicOffices.map(office => (<td key={office} style={{ ...styles.td, textAlign: 'center' }}>{row[office]}</td>))}
                          <td style={{ ...styles.td, textAlign: 'center', fontWeight: '700' }}>{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {isInfoModalOpen && !isGeneratingPDF && (
                    <div className="no-print" style={{ margin: '16px', padding: '16px', backgroundColor: currentTheme.bg, border: `1px dashed ${currentTheme.accent}`, borderRadius: '8px', fontSize: '0.85rem', color: currentTheme.secondary }}>
                      <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>History Flow Logic:</strong> If a candidate had multiple status changes within the date range, only their <b>last status in that date range</b> is counted.</div>
                      <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>Total Application:</strong> Sum of "Application Not planned yet" and "Pending interviews".</div>
                      <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>Application Not planned yet:</strong> Candidates whose last status change within the date range was "Not Planned".</div>
                      <div><strong style={{ color: currentTheme.primary }}>Pending interviews:</strong> Candidates whose last status change within the date range was a "Pending" status (1st/2nd/3rd interview).</div>
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.chartContainer}>
                <div style={{ fontSize: '0.8rem', fontWeight: '700', color: currentTheme.secondary, marginBottom: '16px', textTransform: 'uppercase' }}>
                  Active Pipeline
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={pipelineChartData}
                      cx="50%" cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                      labelLine={true}
                      label={(props) => renderPieLabel(props, currentTheme)}
                      isAnimationActive={false}
                    >
                      {pipelineChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                    <Tooltip content={<CustomTooltip currentTheme={currentTheme} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* 4. Outcomes */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <People style={{ color: currentTheme.accent }} />
              <div style={styles.sectionTitle}>Recruitment Outcomes</div>
            </div>

            <div style={styles.splitLayout}>
              <div style={styles.tableContainer}>
                <div style={styles.card}>
                  <table style={styles.table}>
                    <thead style={styles.thead}>
                      <tr>
                        <th style={{ ...styles.th, width: '40%' }}>Outcome</th>
                        {dynamicOffices.map(office => (<th key={office} style={{ ...styles.th, textAlign: 'center' }}>{office}</th>))}
                        <th style={{ ...styles.th, textAlign: 'center' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outcomeRows.map((row, index) => (
                        <tr key={index} style={{ transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = currentTheme.tableRowHover} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <td style={{ ...styles.td, fontWeight: '600' }}>
                            {row.label}
                            <div style={{ fontSize: '0.75rem', color: currentTheme.secondary, fontWeight: '400' }}>{row.note}</div>
                          </td>
                          {dynamicOffices.map(office => (<td key={office} style={{ ...styles.td, textAlign: 'center' }}>{row[office]}</td>))}
                          <td style={{ ...styles.td, textAlign: 'center', fontWeight: '700' }}>{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {isInfoModalOpen && !isGeneratingPDF && (
                    <div className="no-print" style={{ margin: '16px', padding: '16px', backgroundColor: currentTheme.bg, border: `1px dashed ${currentTheme.accent}`, borderRadius: '8px', fontSize: '0.85rem', color: currentTheme.secondary }}>
                      <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>History Flow Logic:</strong> Uses the same logic as the funnel table (only counts the last status change within the date range).</div>
                      <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>Hired in period:</strong> Candidates whose last status change within the date range was "Hired".</div>
                      <div style={{ marginBottom: '6px' }}><strong style={{ color: currentTheme.primary }}>Declined in period:</strong> Candidates whose last status change within the date range was a "Declined" status.</div>
                      <div><strong style={{ color: currentTheme.primary }}>Reserved list (All Time):</strong> Ignores the date range completely and counts candidates who are <i>currently</i> in "Reserved" status.</div>
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.chartContainer}>
                <div style={{ fontSize: '0.8rem', fontWeight: '700', color: currentTheme.secondary, marginBottom: '16px', textTransform: 'uppercase' }}>
                  Hired vs Declined
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={outcomesChartData}
                      cx="50%" cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                      labelLine={true}
                      label={(props) => renderPieLabel(props, currentTheme)}
                      isAnimationActive={false}
                    >
                      {outcomesChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.name === 'Hired' ? '#22C55E' : '#EF4444'} />
                      ))}
                    </Pie>
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                    <Tooltip content={<CustomTooltip currentTheme={currentTheme} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* 5. Applications Received Today Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <CalendarMonth style={{ color: currentTheme.accent }} />
              <div style={styles.sectionTitle}>Applications Received Today</div>
            </div>

            <div style={styles.splitLayout}>
              <div style={styles.tableContainer}>
                <div style={styles.card}>
                  <table style={styles.table}>
                    <thead style={styles.thead}>
                      <tr>
                        <th style={{ ...styles.th, width: '40%' }}>Applied From</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Nador</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Tangier</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appsTodayData.map((row, index) => (
                        <tr key={index} style={{ transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = currentTheme.tableRowHover} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <td style={{ ...styles.td, fontWeight: '600' }}>
                            {row.source}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{row.Nador}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{row.Tangier}</td>
                          <td style={{ ...styles.td, textAlign: 'center', fontWeight: '700' }}>{row.Total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={styles.chartContainer}>
                <div style={{ fontSize: '0.8rem', fontWeight: '700', color: currentTheme.secondary, marginBottom: '16px', textTransform: 'uppercase' }}>
                  Source Distribution
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  {pieTodayData.length > 0 ? (
                    <PieChart>
                      <Pie
                        data={pieTodayData}
                        cx="50%" cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                        labelLine={true}
                        label={(props) => renderPieLabel(props, currentTheme)}
                        isAnimationActive={false}
                      >
                        {pieTodayData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                      <Tooltip content={<CustomTooltip currentTheme={currentTheme} />} />
                    </PieChart>
                  ) : (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: currentTheme.secondary, fontStyle: 'italic', fontSize: '0.85rem' }}>
                      No applications received today.
                    </div>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* 6. Offboardings Section */}
          <div style={styles.section}>
            <div style={{ ...styles.sectionHeader, borderBottom: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <PersonOff style={{ color: currentTheme.charts.danger }} />
                <span style={styles.sectionTitle}>Offboardings</span>
              </div>
            </div>

            <div style={styles.card}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={{ ...styles.th, width: '30%' }}>Employee</th>
                    <th style={{ ...styles.th, width: '15%' }}>Office</th>
                    <th style={{ ...styles.th, width: '15%' }}>Department</th>
                    <th style={{ ...styles.th, width: '10%' }}>Start Date</th>
                    <th style={{ ...styles.th, width: '10%' }}>Exit Date</th>
                    <th style={{ ...styles.th, width: '20%' }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOffboardings.length > 0 ? (
                    filteredOffboardings.map((off, i) => (
                      <tr key={off.id || i} style={{ transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = currentTheme.tableRowHover} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={{ ...styles.td, fontWeight: '600' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            {off.imageUrl ? (
                              <img src={off.imageUrl} alt={off.employeeName} style={{ width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ width: '90px', height: '90px', borderRadius: '50%', backgroundColor: currentTheme.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${currentTheme.borderColor}` }}>
                                <PersonOff style={{ fontSize: '30px', color: currentTheme.secondary }} />
                              </div>
                            )}
                            <span style={{ fontSize: '1.1rem' }}>{off.employeeName}</span>
                          </div>
                        </td>
                        <td style={styles.td}>{off.office}</td>
                        <td style={styles.td}>{off.department}</td>
                        <td style={styles.td}>{getFormattedDate(off.startDate)}</td>
                        <td style={styles.td}>{getFormattedDate(off.exitDate)}</td>
                        <td style={styles.td}>{off.reason}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ ...styles.td, padding: '40px', textAlign: 'center', color: currentTheme.secondary, fontStyle: 'italic' }}>
                        No offboardings found with exit dates in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <div style={styles.footer}>
          Generated by Axopeople on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* === EDIT MODAL === */}
      <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Review & Edit Report Data</DialogTitle>
        <DialogContent dividers>
          <div style={{ marginBottom: '30px' }}>
            <h4 style={{ color: currentTheme.accent, marginBottom: '15px' }}>Workforce Staffing</h4>
            {summaryData.map(row => (
              <div key={row.office} style={{ display: 'flex', gap: '15px', marginBottom: '15px', alignItems: 'center' }}>
                <div style={{ width: 100, fontWeight: 'bold' }}>{row.office}</div>
                <TextField label="Current Ops" size="small" type="number"
                  value={overrides[`summary-currentOps-${row.office}`] ?? row.currentOps}
                  onChange={(e) => handleOverrideChange(`summary-currentOps-${row.office}`, e.target.value)} />
                <TextField label="Hired (Pending)" size="small" type="number"
                  value={overrides[`summary-hiredPending-${row.office}`] ?? row.hiredPending}
                  onChange={(e) => handleOverrideChange(`summary-hiredPending-${row.office}`, e.target.value)} />
                <TextField label="HQ Staff" size="small" type="number"
                  value={overrides[`summary-hqStaff-${row.office}`] ?? row.hqStaff}
                  onChange={(e) => handleOverrideChange(`summary-hqStaff-${row.office}`, e.target.value)} />
                <TextField label="Facility" size="small" type="number"
                  value={overrides[`summary-facilityStaff-${row.office}`] ?? row.facilityStaff}
                  onChange={(e) => handleOverrideChange(`summary-facilityStaff-${row.office}`, e.target.value)} />
              </div>
            ))}
          </div>

          <div style={{ marginBottom: '30px' }}>
            <h4 style={{ color: currentTheme.accent, marginBottom: '15px' }}>Pipeline Overview</h4>
            {pipelineRows.map(row => (
              <div key={row._key} style={{ display: 'flex', gap: '15px', marginBottom: '15px', alignItems: 'center' }}>
                <div style={{ width: 150, fontWeight: 'bold' }}>{row.label}</div>
                {dynamicOffices.map(office => (
                  <TextField key={office} label={office} size="small" type="number"
                    value={overrides[`${row._key}-${office}`] ?? row[office]}
                    onChange={(e) => handleOverrideChange(`${row._key}-${office}`, e.target.value)} />
                ))}
              </div>
            ))}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ color: currentTheme.accent, marginBottom: '15px' }}>Outcomes</h4>
            {outcomeRows.map(row => (
              <div key={row._key} style={{ display: 'flex', gap: '15px', marginBottom: '15px', alignItems: 'center' }}>
                <div style={{ width: 150, fontWeight: 'bold' }}>{row.label}</div>
                {dynamicOffices.map(office => (
                  <TextField key={office} label={office} size="small" type="number"
                    value={overrides[`${row._key}-${office}`] ?? row[office]}
                    onChange={(e) => handleOverrideChange(`${row._key}-${office}`, e.target.value)} />
                ))}
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOverrides({})}>Reset to Auto-Calculated</Button>
          <div style={{ flex: 1 }}></div>
          <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            setIsEditModalOpen(false);
            setTimeout(exportPDF, 300); // Allow modal to close visually before PDF generation
          }} sx={{ bgcolor: currentTheme.accent, '&:hover': { bgcolor: '#2563EB' } }}>
            Confirm & Export
          </Button>
        </DialogActions>
      </Dialog>


    </LocalizationProvider>
  );
};

export default CandidateReportGenerator;
